import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import React, { useEffect, useId, useState } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
  TooltipContentProps,
} from 'recharts';
import { ChartData, CycleStats, CycleModelResults } from '../types';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import NoteMarkerOverlay, { SeriesNotes } from './NoteMarkerOverlay';
import { ChartCopy, ChartUnavailable, explainEmpty } from './chartUnavailable';
import ChartCsvButton from './ChartCsvButton';
import Button from './ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';
import { CsvColumn } from '../utils/chartCsv';
import { ANALYSIS_CHART_LEFT_MARGIN, ANALYSIS_LEGEND_WRAPPER_STYLE, RUNTIME_CHART_RIGHT_MARGIN, RUNTIME_SERIES_Z_INDEX } from './analysisChartLayout';

const TITLE = 'Heat/Cool Runtimes and Outdoor Temperatures';

const COPY: ChartCopy = {
  description:
    'This chart shows how long the system ran in each mode, split out by ' +
    'heating, cooling and continuous circulation.',
  measurement: 'run cycle',
};

interface RuntimeHeatCoolChartProps {
  jsonData: ChartData;
  deviceName?: string;
  showEmptySeries?: boolean;
  /** Filename part for the CSV download; names the cycle model when one is picked. */
  csvChart?: string;
  noteSeries?: SeriesNotes[];
  onNoteImageClick?: (noteId: number) => void;
}

interface ChartDataPoint {
  date: string;
  coolMins: number | null;
  heatMins: number | null;
  contMins: number | null;
  unknownMins: number | null;
  coolData?: CycleStats['cool'];
  heatData?: CycleStats['heat'];
  contData?: CycleStats['cont'];
  unknownData?: CycleStats['unknown'];
  maxTemp: number | null;
  minTemp: number | null;
}

const celsiusToFahrenheit = (celsius: number): number => {
  return celsius * 9 / 5 + 32;
};

const CustomTooltip = ({ active, payload, label }: TooltipContentProps) => {
  if (!active || !payload || payload.length === 0) return null;

  const dataPoint = payload[0].payload as ChartDataPoint;

  return (
    <div className="bg-white p-3 border border-border rounded-sm shadow-lg">
      <p className="font-semibold mb-2">{label}</p>

      {([
        ['Cool Mode', dataPoint.coolData, 'text-link'],
        ['Heat Mode', dataPoint.heatData, 'text-status-warning-foreground'],
        ['Continuous Mode', dataPoint.contData, 'text-gray-600'],
        ['Unknown', dataPoint.unknownData, 'text-purple-600'],
      ] as const).map(([label, mode, color]) => mode && (
        <div key={label} className="mb-2 text-sm">
          <p className={`font-medium ${color}`}>{label}</p>
          <p>Runtime: {mode.mins} min ({(mode.mins / 60).toFixed(1)} hrs)</p>
          {mode.cycles != null && <p>Cycles: {mode.cycles}</p>}
          {mode.pressure != null && <p>Pressure: {mode.pressure.toFixed(2)} Pa</p>}
          {mode.min_temp != null && mode.max_temp != null && (
            <p>Temp Range: {celsiusToFahrenheit(mode.min_temp).toFixed(1)}°F - {celsiusToFahrenheit(mode.max_temp).toFixed(1)}°F</p>
          )}
          {mode.lag != null && <p>Lag: {mode.lag.toFixed(2)} min</p>}
        </div>
      ))}

      {(dataPoint.maxTemp !== null || dataPoint.minTemp !== null) && (
        <div className="text-sm border-t pt-2 mt-2">
          <p className="font-medium">Outdoor Temperature</p>
          {dataPoint.maxTemp !== null && <p className="text-status-error-foreground">High: {dataPoint.maxTemp}°F</p>}
          {dataPoint.minTemp !== null && <p className="text-status-success-foreground">Low: {dataPoint.minTemp}°F</p>}
        </div>
      )}
    </div>
  );
};

export const buildRuntimeChartData = (jsonData: ChartData): ChartDataPoint[] => (jsonData.dates || []).map((date, i) => {
    const cycleData = jsonData.cycle_stats?.[i];
    return {
      date,
      coolMins: cycleData == null ? null : cycleData.cool?.mins ?? 0,
      heatMins: cycleData == null ? null : cycleData.heat?.mins ?? 0,
      contMins: cycleData == null ? null : cycleData.cont?.mins ?? 0,
      unknownMins: cycleData == null ? null : cycleData.unknown?.mins ?? 0,
      unknownData: cycleData?.unknown,
      coolData: cycleData?.cool,
      heatData: cycleData?.heat,
      contData: cycleData?.cont,
      maxTemp: jsonData.temperatures?.max?.[i] ?? null,
      minTemp: jsonData.temperatures?.min?.[i] ?? null,
    };
  });

// Everything the tooltip shows per mode, one column group per mode.
const CSV_MODES = [
  ['Cool', 'coolData', 'coolMins'],
  ['Heat', 'heatData', 'heatMins'],
  ['Continuous', 'contData', 'contMins'],
  ['Unknown', 'unknownData', 'unknownMins'],
] as const;

const optionalF = (celsius: number | null | undefined): number | null =>
  celsius == null ? null : celsiusToFahrenheit(celsius);

const RUNTIME_HEAT_COOL_CSV_COLUMNS: CsvColumn<ChartDataPoint>[] = [
  { header: 'Date', value: 'date' },
  ...CSV_MODES.flatMap(([label, key, minsKey]) => [
    { header: `${label} runtime (min)`, value: minsKey },
    { header: `${label} cycles`, value: (p: ChartDataPoint) => p[key]?.cycles ?? null },
    { header: `${label} pressure (Pa)`, value: (p: ChartDataPoint) => p[key]?.pressure ?? null },
    { header: `${label} min temperature (°F)`, value: (p: ChartDataPoint) => optionalF(p[key]?.min_temp) },
    { header: `${label} max temperature (°F)`, value: (p: ChartDataPoint) => optionalF(p[key]?.max_temp) },
    { header: `${label} lag (min)`, value: (p: ChartDataPoint) => p[key]?.lag ?? null },
  ]),
  { header: 'Outdoor max temperature (°F)', value: 'maxTemp' },
  { header: 'Outdoor min temperature (°F)', value: 'minTemp' },
];

const RuntimeHeatCoolPlot: React.FC<RuntimeHeatCoolChartProps> = ({
  jsonData,
  showEmptySeries = false,
  csvChart = 'runtime-heat-cool',
  noteSeries,
  onNoteImageClick,
}) => {
  const activeDot = useAnalysisActiveDot(true);
  // Legacy bail-out, now only for payloads carrying neither values nor a status
  // block — otherwise the panel explains itself below.
  if (!jsonData || (!jsonData.cycle_stats && !jsonData.cycle_stats_status)) return null;

  const chartData = buildRuntimeChartData(jsonData);
  const validTemps = chartData.reduce((temps, point) => {
    if (point.maxTemp !== null && point.maxTemp !== undefined) temps.push(point.maxTemp);
    if (point.minTemp !== null && point.minTemp !== undefined) temps.push(point.minTemp);
    return temps;
  }, [] as number[]);

  const tempDomain = validTemps.length > 0 ? {
    min: Math.floor(Math.min(...validTemps)),
    max: Math.ceil(Math.max(...validTemps))
  } : null;

  const hasTemperatures = tempDomain !== null;
  const tempPadding = tempDomain ? Math.max(5, (tempDomain.max - tempDomain.min) * 0.1) : 0;

  const hasCycleStats = chartData.some(d => (d.coolMins ?? 0) > 0 || (d.heatMins ?? 0) > 0 || (d.contMins ?? 0) > 0 || (d.unknownMins ?? 0) > 0);

  // Nothing to stack. The pipeline computes cycle_stats inside the same
  // fan-separation guard as the transition deltas, so ~12 of 66 devices land
  // here on any given month and used to get no chart and no explanation at all.
  if (!hasCycleStats && !showEmptySeries) {
    const explanation = explainEmpty(jsonData.cycle_stats_status, COPY);
    if (explanation) {
      return (
        <div className="w-full mt-4 mb-8">
          <h3 className="text-lg font-semibold mb-4">{TITLE}</h3>
          <ChartUnavailable {...explanation} />
        </div>
      );
    }
    return null;
  }

  return (
    <div className="w-full mt-4 mb-8">
      <div className="flex items-start justify-between gap-2 mb-8">
        <h3 className="text-lg font-semibold">{TITLE}</h3>
        <ChartCsvButton chart={csvChart} rows={chartData} columns={RUNTIME_HEAT_COOL_CSV_COLUMNS} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1" style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={chartData}
              margin={{ top: 10, right: RUNTIME_CHART_RIGHT_MARGIN, left: ANALYSIS_CHART_LEFT_MARGIN, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" yAxisId="runtime" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={50}
              />
              <YAxis
                yAxisId="runtime"
                domain={[0, 'auto']}
                label={{
                  value: 'Runtime (minutes)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -35,
                  style: { textAnchor: 'middle' }
                }}
              />
              {hasTemperatures && tempDomain && (
                <YAxis
                  yAxisId="temperature"
                  orientation="right"
                  label={{
                    value: 'Temperature (°F)',
                    angle: 90,
                    position: 'insideRight',
                    offset: 75,
                    style: { textAnchor: 'middle' }
                  }}
                  domain={[tempDomain.min - tempPadding, tempDomain.max + tempPadding]}
                />
              )}
              <Tooltip content={CustomTooltip} />
              <Legend
                verticalAlign="top"
                height={20}
                itemSorter={null}
                wrapperStyle={ANALYSIS_LEGEND_WRAPPER_STYLE}
              />
              <Bar
                dataKey="coolMins"
                fill="var(--chart-cool)"
                name="Cool Mode"
                yAxisId="runtime"
                zIndex={RUNTIME_SERIES_Z_INDEX}
                stackId="runtime"
              />
              <Bar
                dataKey="heatMins"
                fill="var(--chart-heat)"
                name="Heat Mode"
                yAxisId="runtime"
                zIndex={RUNTIME_SERIES_Z_INDEX}
                stackId="runtime"
              />
              <Bar
                dataKey="contMins"
                fill="#6b7280"
                name="Continuous Mode"
                yAxisId="runtime"
                zIndex={RUNTIME_SERIES_Z_INDEX}
                stackId="runtime"
              />
              <Bar dataKey="unknownMins" fill="#a78bfa" name="Unknown" yAxisId="runtime" zIndex={RUNTIME_SERIES_Z_INDEX} stackId="runtime" />
              {hasTemperatures && (
                <>
                  <Line
                    type="monotone"
                    dataKey="maxTemp"
                    stroke="#dc2626"
                    name="Max Temperature"
                    yAxisId="temperature"
                    zIndex={RUNTIME_SERIES_Z_INDEX}
                    dot={true}
                    activeDot={activeDot}
                    connectNulls={true}
                  />
                  <Line
                    type="monotone"
                    dataKey="minTemp"
                    stroke="#059669"
                    name="Min Temperature"
                    yAxisId="temperature"
                    zIndex={RUNTIME_SERIES_Z_INDEX}
                    dot={true}
                    activeDot={activeDot}
                    connectNulls={true}
                  />
                </>
              )}
              {noteSeries && noteSeries.length > 0 && onNoteImageClick && (
                <NoteMarkerOverlay seriesNotes={noteSeries} onImageClick={onNoteImageClick} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const RuntimeHeatCoolChart: React.FC<RuntimeHeatCoolChartProps> = (props) => {
  const { jsonData, deviceName } = props;
  const options = jsonData.cycle_model_options ?? [];
  // FS-860: a Clip the heat/cool model scores opens on the model; the
  // temperature-trend classifier stays one menu choice away.
  const defaultSelection = options[0]?.id ?? 'current';
  const [selected, setSelected] = useState(defaultSelection);
  const [saved, setSaved] = useState<{ key: string; result: CycleModelResults } | null>(null);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const selectId = useId();
  const dates = jsonData.dates ?? [];
  const start = dates[0];
  const end = dates[dates.length - 1];
  const option = options.find((o) => o.id === selected);
  const requestKey = `${deviceName}|${selected}|${start}|${end}`;

  useEffect(() => { setSelected(defaultSelection); }, [deviceName, defaultSelection]);
  useEffect(() => {
    if (selected !== 'current' && !option) setSelected('current');
  }, [selected, option]);
  useEffect(() => {
    if (selected === 'current' || !deviceName || !start || !end || !option) return;
    const controller = new AbortController();
    let cancelled = false;
    setSaved(null);
    setFailure(null);
    axiosInstance.get<CycleModelResults>(`${config.fetchDailyApiUrl}/${encodeURIComponent(deviceName)}`, {
      params: { mode: 'cycle_model', cycle_model: selected, start_date: start, end_date: end },
      signal: controller.signal,
    }).then(({ data }) => {
      if (!cancelled) setSaved({ key: requestKey, result: data });
    }).catch((error) => {
      if (!cancelled) setFailure({ key: requestKey, message: extractErrorMessage(error, 'Could not load model results.') });
    });
    return () => { cancelled = true; controller.abort(); };
  // option is represented by its stable id so a parent render does not repeat the request.
  }, [deviceName, selected, start, end, requestKey, option?.id, attempt]);

  if (!deviceName || options.length === 0) return <RuntimeHeatCoolPlot {...props} />;
  const result = saved?.key === requestKey ? saved.result : null;
  const error = failure?.key === requestKey ? failure.message : null;
  const byDate = new Map(result?.dates.map((d, i) => [d, result.cycle_stats[i]]) ?? []);
  const aligned = dates.map((d) => byDate.get(d) ?? null);
  const available = aligned.filter((d) => d != null).length;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <label htmlFor={selectId} className="text-sm font-medium">Cycle model</label>
        <Select value={selected} items={[{ value: 'current', label: 'Previous classifier' }, ...options.map((model) => ({ value: model.id, label: model.label }))]} onValueChange={(value) => { if (value !== null) setSelected(value); }}>
          <SelectTrigger id={selectId} aria-describedby={`${selectId}-help`} title="Choose which cycle classification model supplies this chart"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="current">Previous classifier</SelectItem>
            {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p id={`${selectId}-help`} className="text-xs text-muted-foreground">Changes the heat and cool runtime series shown below.</p>
      </div>
      {selected === 'current' ? <RuntimeHeatCoolPlot {...props} /> : (
        <>
          <p className="text-xs text-muted-foreground mt-2">
            Experimental results use {option?.source_analysis_name ?? "this device's daily analysis"} inputs.
          </p>
          {error ? (
            <div role="alert" className="mt-3 border border-status-error-border rounded-sm p-3 text-red-700">
              {error}
              <Button
                variant="link"
                className="ml-3"
                onClick={() => setAttempt((n) => n + 1)}
                title="Ask the server for this model's results again"
              >
                Retry
              </Button>
            </div>
          ) : !start || !end || (result && available === 0) ? (
            <p role="status" className="mt-4 text-sm text-gray-600">No results from this model in the analyzed date range. Choose Previous classifier to see this range.</p>
          ) : !result ? (
            <p role="status" className="mt-4 text-sm text-gray-600">Loading model results…</p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mt-2">{available} of {dates.length} analyzed days have results. Missing dates appear as gaps. The model scores each day three days after it ends.</p>
              <RuntimeHeatCoolPlot {...props} csvChart={`runtime-heat-cool-${selected}`} showEmptySeries jsonData={{ ...jsonData, cycle_stats: aligned, cycle_stats_status: undefined }} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RuntimeHeatCoolChart;
